import { createSeededRng } from '../../domain/seededRandom.js';
import type { CharacterDraft, CharacterDraftMeta, WorldDraft, WorldSnapshot } from '../../domain/types.js';
import type { Npc } from '../../types.js';
import {
  bodyFromCharacterDraft,
  bodyFromNpc,
  defaultBodyForMeta,
  localUid,
  npcFromCharacterParts,
  type CharacterBodySnapshot,
} from './characterBody.js';

const FIRST_NAMES = ['Mara', 'Jonas', 'Priya', 'Aiko', 'Ellis', 'Wren', 'Lena', 'Iris', 'Felix', 'Zadie'];
const INITIALS = ['A', 'B', 'C', 'K', 'M', 'R', 'S', 'T', 'V', 'W'];

function pick<T>(items: readonly T[], rng: () => number): T {
  return items[Math.floor(rng() * items.length)]!;
}

/** Deterministic privacy-safe identity for offline fallback. */
export function randomizeCharacterBasic(
  draft: WorldDraft,
  index: number,
): CharacterDraft['basic'] {
  const rng = createSeededRng(`${draft.draftId}:char:${index}`);
  return {
    firstName: pick(FIRST_NAMES, rng),
    lastInitial: pick(INITIALS, rng),
  };
}

/** @deprecated v1 helper — use randomizeCharacterBasic + v2 draft ops. */
export function randomizeCharacterDraft(
  draft: WorldDraft,
  index: number,
): {
  firstName: string;
  lastInitial?: string;
  species: string;
  sex: 'M' | 'F';
  ageRange: string;
  status: 'draft';
  source: 'fallback';
} {
  const basic = randomizeCharacterBasic(draft, index);
  return {
    ...basic,
    species: 'Human',
    sex: 'M',
    ageRange: 'Adult',
    status: 'draft',
    source: 'fallback',
  };
}

export function randomizeCharacterBody(
  draft: WorldDraft,
  character: CharacterDraft,
): CharacterBodySnapshot {
  const fromDraft = bodyFromCharacterDraft(character);
  if (fromDraft) return fromDraft;
  return defaultBodyForMeta(draft.draftId, {
    draftCharacterId: character.draftCharacterId,
    species: 'Human',
    sex: 'M',
    ageRange: 'Adult',
  });
}

export { bodyFromNpc, defaultBodyForMeta, bodyFromCharacterDraft, type CharacterBodySnapshot };

/** Map v2 character draft + body sidecar into a playable NPC. */
export function characterDraftToNpc(
  character: CharacterDraft,
  body: CharacterBodySnapshot,
  index: number,
  snapshot: WorldSnapshot,
): Npc {
  return npcFromCharacterParts(character, body, index, snapshot.worldId);
}

function asV2Character(character: CharacterDraft | CharacterDraftMeta): CharacterDraft {
  if ('basic' in character) return character;
  return {
    draftCharacterId: character.draftCharacterId,
    schemaVersion: character.schemaVersion,
    basic: { firstName: character.firstName, lastInitial: character.lastInitial },
    appearance: null,
    nature: null,
    traits: [],
    status: character.status,
    source: character.source,
    dependencyIds: [],
    updatedAt: character.updatedAt,
  };
}

function identityFromSidecarOrMeta(
  raw: CharacterDraft | CharacterDraftMeta,
  sidecar?: CharacterBodySnapshot,
): Pick<CharacterDraftMeta, 'species' | 'sex' | 'ageRange'> {
  if ('basic' in raw && (raw.sex || raw.species || raw.ageRange)) {
    return {
      species: raw.species ?? sidecar?.species ?? 'Human',
      sex: raw.sex ?? sidecar?.sex ?? 'M',
      ageRange: raw.ageRange ?? sidecar?.ageRange ?? 'Adult',
    };
  }
  if (sidecar) {
    return { species: sidecar.species, sex: sidecar.sex, ageRange: sidecar.ageRange };
  }
  if ('species' in raw) {
    return {
      species: raw.species ?? 'Human',
      sex: raw.sex ?? 'M',
      ageRange: raw.ageRange ?? 'Adult',
    };
  }
  return { species: 'Human', sex: 'M', ageRange: 'Adult' };
}

export function buildLaunchRoster(
  characters: Array<CharacterDraft | CharacterDraftMeta>,
  bodies: Record<string, CharacterBodySnapshot>,
  snapshot: WorldSnapshot,
  draftId: string,
): Npc[] {
  return characters
    .filter((c) => c.status !== 'removed')
    .map((raw, i) => {
      const c = asV2Character(raw);
      const sidecar = bodies[c.draftCharacterId];
      const identity = identityFromSidecarOrMeta(raw, sidecar);
      const body =
        sidecar
        ?? bodyFromCharacterDraft(c, identity.species, identity.sex)
        ?? defaultBodyForMeta(draftId, {
          draftCharacterId: c.draftCharacterId,
          ...identity,
        });
      return characterDraftToNpc(c, body, i, snapshot);
    });
}

/** Verify roster preserves appearance, nature sliders, and traits from body snapshots. */
export function rosterPreservesBodies(
  roster: Npc[],
  bodies: Record<string, CharacterBodySnapshot>,
  characters: CharacterDraft[],
): boolean {
  const active = characters.filter((c) => c.status !== 'removed');
  return active.every((c, i) => {
    const npc = roster[i];
    const body = bodies[c.draftCharacterId] ?? bodyFromCharacterDraft(c);
    if (!npc || !body) return false;
    return (
      npc.palette.shirt === body.palette.shirt
      && npc.nature.sociability === body.nature.sociability
      && npc.nurture.length === body.nurture.length
    );
  });
}

/** Stable id for tests — re-export for adapter draft ops parity. */
export { localUid };
